import React, { useCallback, useEffect, useRef } from 'react';

import { useStyles } from './OtpInput.styles';

interface OtpInputProps {
  value: string;
  onChange: (next: string) => void;
  length?: number;
  disabled?: boolean;
  autoFocusFirstCell?: boolean;
  hasError?: boolean;
  ariaLabelPrefix?: string;
  'aria-describedby'?: string;
  className?: string;
}

function toCells(value: string, length: number): string[] {
  const cells = Array<string>(length).fill('');
  for (let i = 0; i < Math.min(value.length, length); i += 1) {
    cells[i] = value[i] ?? '';
  }
  return cells;
}

export function OtpInput({
  value,
  onChange,
  length = 6,
  disabled = false,
  autoFocusFirstCell = false,
  hasError = false,
  ariaLabelPrefix = 'Digit',
  'aria-describedby': ariaDescribedBy,
  className,
}: OtpInputProps): JSX.Element {
  const classes = useStyles();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const cells = toCells(value, length);

  useEffect(() => {
    if (autoFocusFirstCell) {
      inputRefs.current[0]?.focus();
    }
  }, [autoFocusFirstCell]);

  const emit = useCallback(
    (next: string[]) => {
      onChange(next.join(''));
    },
    [onChange]
  );

  const handleInput = useCallback(
    (index: number, raw: string) => {
      const digit = raw.replace(/\D/g, '').slice(-1);
      const next = [...cells];
      next[index] = digit;
      emit(next);
      if (digit && index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [cells, emit, length]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        if (!cells[index] && index > 0) {
          inputRefs.current[index - 1]?.focus();
          const next = [...cells];
          next[index - 1] = '';
          emit(next);
        } else {
          const next = [...cells];
          next[index] = '';
          emit(next);
        }
      } else if (e.key === 'ArrowLeft' && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else if (e.key === 'ArrowRight' && index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [cells, emit, length]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '');
      if (!pasted) return;
      const digits = pasted.slice(0, length).split('');
      const next = [...cells];
      digits.forEach((digit, i) => {
        next[i] = digit;
      });
      emit(next);
      const focusIndex = Math.min(digits.length, length - 1);
      inputRefs.current[focusIndex]?.focus();
    },
    [cells, emit, length]
  );

  const inputClassName = `${classes.input} ${hasError ? classes.inputError : ''}`.trim();

  return (
    <div className={className ?? classes.container}>
      {Array.from({ length }).map((_, index) => (
        <input
          key={`otp-cell-${index}`}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          className={inputClassName}
          value={cells[index]}
          onChange={(e) => handleInput(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          aria-label={`${ariaLabelPrefix} ${index + 1}`}
          aria-describedby={ariaDescribedBy}
        />
      ))}
    </div>
  );
}
